// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

import { Badge, Button, Dropdown, FloatButton, Input, Modal, Select, Space, Typography, type MenuProps } from "antd";
import { NumberOutlined, PlusOutlined, EditOutlined, MoreOutlined, AppstoreOutlined, AppstoreAddOutlined, CaretDownOutlined, CaretRightOutlined, LoginOutlined, UserOutlined } from "@ant-design/icons";
import { useState } from "react";
import { Power, type Room } from "../types";
import type { SpaceChildView } from "./Workspace";

// RoomList is the left rail: channels grouped by Space (Telegram-style forum
// topics / Discord-style servers). Channels in no space are listed ungrouped at
// the top; each Space is a collapsible section. Right-click (or ⋮) a channel for
// operator actions; "+" on a space header creates a channel inside it.
export function RoomList({
  spaces,
  childrenBySpace,
  ungrouped,
  dms = [],
  invites = [],
  onAcceptInvite,
  onDeclineInvite,
  active,
  unread,
  onSelect,
  onJoin,
  onJoinChild,
  onLeave,
  onCreate,
  onCreateSpace,
  myPowerByRoom,
  onSetJoinRule,
  filter = "",
  fab = false,
}: {
  readonly spaces: Room[];
  readonly childrenBySpace: Record<string, SpaceChildView[]>;
  readonly ungrouped: Room[];
  // dms are 1:1 direct-message rooms, each pre-named with the counter-party.
  readonly dms?: Room[];
  // invites are pending (non-DM) channel/space invites to accept or decline.
  readonly invites?: { room_id: string; name: string; inviter: string }[];
  readonly onAcceptInvite?: (roomID: string) => void;
  readonly onDeclineInvite?: (roomID: string) => void;
  readonly active: string | null;
  readonly unread: Record<string, number>;
  readonly onSelect: (id: string) => void;
  readonly onJoin: (id: string) => void;
  readonly onJoinChild: (id: string) => void;
  readonly onLeave: (id: string) => void;
  readonly onCreate: (name: string, opts?: { parentSpace?: string }) => void;
  readonly onCreateSpace: (name: string) => void;
  readonly myPowerByRoom: (roomID: string) => number;
  readonly onSetJoinRule: (roomID: string, rule: string) => void;
  // filter narrows the list to channels/spaces whose name matches (mobile search).
  readonly filter?: string;
  // fab shows a floating create button (mobile) instead of the rail-head buttons.
  readonly fab?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [spaceOpen, setSpaceOpen] = useState(false);
  const [value, setValue] = useState("");
  const [parentSpace, setParentSpace] = useState<string>("");
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  function confirmJoin() {
    const id = value.trim();
    if (id) onJoin(id);
    setValue("");
    setOpen(false);
  }
  function confirmCreate() {
    const name = value.trim();
    if (name) onCreate(name, parentSpace ? { parentSpace } : undefined);
    setValue("");
    setCreateOpen(false);
  }
  function confirmCreateSpace() {
    const name = value.trim();
    if (name) onCreateSpace(name);
    setValue("");
    setSpaceOpen(false);
  }
  function openCreate(space: string) {
    setValue("");
    setParentSpace(space);
    setCreateOpen(true);
  }
  function toggle(id: string) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function channelRow(r: Room) {
    const count = unread[r.id] ?? 0;
    const btn = (
      <button type="button" className={`room-item ${r.id === active ? "room-active" : ""}`} onClick={() => onSelect(r.id)}>
        <NumberOutlined className="room-hash" />
        <span className="room-name">{r.name}</span>
        {count > 0 && <Badge count={count} size="small" className="room-unread" />}
      </button>
    );
    // Everyone can leave; operators additionally get the access-policy controls.
    const isOp = myPowerByRoom(r.id) >= Power.Op;
    const items: MenuProps["items"] = [
      ...(isOp
        ? [
            { key: "label", disabled: true, label: "Access policy" },
            { key: "public", label: "Public", onClick: () => onSetJoinRule(r.id, "public") },
            { key: "invite", label: "Invite-only", onClick: () => onSetJoinRule(r.id, "invite") },
            { key: "knock", label: "Knockable", onClick: () => onSetJoinRule(r.id, "knock") },
            { type: "divider" as const },
          ]
        : []),
      { key: "leave", danger: true, label: "Leave channel", onClick: () => onLeave(r.id) },
    ];
    return (
      <div className="room-row" key={r.id}>
        <Dropdown trigger={["contextMenu"]} menu={{ items }}>
          {btn}
        </Dropdown>
        <Dropdown trigger={["click"]} menu={{ items }} placement="bottomRight">
          <button type="button" className="room-actions" title="Channel actions">
            <MoreOutlined />
          </button>
        </Dropdown>
      </div>
    );
  }

  // A direct-message row: a person icon + the counter-party's name (already computed
  // per-viewer) + unread; the only action is to leave.
  function dmRow(r: Room) {
    const count = unread[r.id] ?? 0;
    const items: MenuProps["items"] = [{ key: "leave", danger: true, label: "Leave conversation", onClick: () => onLeave(r.id) }];
    return (
      <div className="room-row" key={r.id}>
        <Dropdown trigger={["contextMenu"]} menu={{ items }}>
          <button type="button" className={`room-item ${r.id === active ? "room-active" : ""}`} onClick={() => onSelect(r.id)}>
            <UserOutlined className="room-hash" />
            <span className="room-name">{r.name}</span>
            {count > 0 && <Badge count={count} size="small" className="room-unread" />}
          </button>
        </Dropdown>
        <Dropdown trigger={["click"]} menu={{ items }} placement="bottomRight">
          <button type="button" className="room-actions" title="Conversation actions">
            <MoreOutlined />
          </button>
        </Dropdown>
      </div>
    );
  }

  // A child channel the user hasn't joined yet: discoverable within the space,
  // one click to join (it isn't listed in the public directory).
  function joinableRow(kid: SpaceChildView) {
    return (
      <button
        type="button"
        className="room-item room-joinable"
        key={kid.id}
        title="Join this channel"
        onClick={() => onJoinChild(kid.id)}
      >
        <NumberOutlined className="room-hash" />
        <span className="room-name">{kid.name}</span>
        <LoginOutlined className="room-join-icon" />
      </button>
    );
  }

  // Mobile channel search: narrow to matching channels/spaces. A space shows if
  // its own name matches (then all its children) or any child matches (then just
  // those). While filtering, sections stay expanded so matches are visible.
  const q = filter.trim().toLowerCase();
  const matches = (name: string) => !q || name.toLowerCase().includes(q);
  const shownUngrouped = ungrouped.filter((r) => matches(r.name));
  const shownDms = dms.filter((r) => matches(r.name));
  const shownSpaces = spaces
    .map((sp) => {
      const kids = childrenBySpace[sp.id] ?? [];
      const spaceMatches = matches(sp.name);
      const shownKids = !q || spaceMatches ? kids : kids.filter((k) => matches(k.name));
      return { sp, kids: shownKids, include: !q || spaceMatches || shownKids.length > 0 };
    })
    .filter((x) => x.include);
  const empty = shownUngrouped.length === 0 && shownSpaces.length === 0;

  return (
    <div className="rail">
      <div className="rail-head">
        <Typography.Text strong>Channels</Typography.Text>
        {!fab && (
          <Space size={4}>
            <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openCreate("")} title="Create a channel" />
            <Button size="small" type="text" icon={<AppstoreAddOutlined />} onClick={() => { setValue(""); setSpaceOpen(true); }} title="Create a space" />
            <Button size="small" type="text" icon={<PlusOutlined />} onClick={() => { setValue(""); setOpen(true); }} title="Join a channel" />
          </Space>
        )}
      </div>
      <div className="rail-list">
        {invites.length > 0 && (
          <div className="rail-group">
            <div className="rail-subhead">Invites</div>
            {invites.map((inv) => (
              <div className="invite-row" key={inv.room_id}>
                <div className="invite-info">
                  <span className="invite-name">{inv.name}</span>
                  {inv.inviter && <span className="invite-from">from {inv.inviter.replace(/^@/, "").split(":")[0]}</span>}
                </div>
                <Space size={4}>
                  <Button size="small" type="primary" onClick={() => onAcceptInvite?.(inv.room_id)}>
                    Join
                  </Button>
                  <Button size="small" onClick={() => onDeclineInvite?.(inv.room_id)}>
                    Decline
                  </Button>
                </Space>
              </div>
            ))}
          </div>
        )}
        {shownDms.length > 0 && (
          <div className="rail-group">
            <div className="rail-subhead">Direct messages</div>
            {shownDms.map(dmRow)}
          </div>
        )}
        {empty && <div className="rail-empty">{q ? "No matches" : "No channels — create one"}</div>}
        {shownUngrouped.map(channelRow)}
        {shownSpaces.map(({ sp, kids }) => {
          const isCollapsed = !q && collapsed.has(sp.id);
          return (
            <div className="space-group" key={sp.id}>
              <div className={`space-head ${sp.id === active ? "space-head-active" : ""}`}>
                <button
                  type="button"
                  className="space-caret"
                  title={isCollapsed ? "Expand" : "Collapse"}
                  onClick={() => toggle(sp.id)}
                >
                  {isCollapsed ? <CaretRightOutlined /> : <CaretDownOutlined />}
                </button>
                <button type="button" className="space-toggle" title="Open this space" onClick={() => onSelect(sp.id)}>
                  <AppstoreOutlined className="space-icon" />
                  <span className="space-name">{sp.name}</span>
                </button>
                {myPowerByRoom(sp.id) >= Power.Op && (
                  <button type="button" className="space-add" title="Add a channel to this space" onClick={() => openCreate(sp.id)}>
                    <PlusOutlined />
                  </button>
                )}
              </div>
              {!isCollapsed && (
                <div className="space-children">
                  {kids.length === 0 && <div className="rail-empty">empty space</div>}
                  {kids.map((kid) => (kid.room ? channelRow(kid.room) : joinableRow(kid)))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {fab && (
        <FloatButton.Group trigger="click" icon={<PlusOutlined />} className="rail-fab">
          <FloatButton className="fab-channel" icon={<EditOutlined />} tooltip={{ title: "New channel", placement: "left" }} onClick={() => openCreate("")} />
          <FloatButton className="fab-space" icon={<AppstoreAddOutlined />} tooltip={{ title: "New space", placement: "left" }} onClick={() => { setValue(""); setSpaceOpen(true); }} />
          <FloatButton className="fab-join" icon={<LoginOutlined />} tooltip={{ title: "Join a channel", placement: "left" }} onClick={() => { setValue(""); setOpen(true); }} />
        </FloatButton.Group>
      )}
      <Modal title="Join a channel" open={open} onOk={confirmJoin} onCancel={() => setOpen(false)} okText="Join">
        <Input placeholder="!room:zalmoxis.local" value={value} onChange={(e) => setValue(e.target.value)} onPressEnter={confirmJoin} autoFocus />
      </Modal>
      <Modal title="Create a channel" open={createOpen} onOk={confirmCreate} onCancel={() => setCreateOpen(false)} okText="Create">
        <Input placeholder="channel name" value={value} onChange={(e) => setValue(e.target.value)} onPressEnter={confirmCreate} autoFocus />
        {spaces.length > 0 && (
          <Select
            className="space-select"
            style={{ width: "100%", marginTop: 12 }}
            value={parentSpace}
            onChange={setParentSpace}
            options={[{ value: "", label: "No space" }, ...spaces.map((s) => ({ value: s.id, label: `Space: ${s.name}` }))]}
          />
        )}
      </Modal>
      <Modal title="Create a space" open={spaceOpen} onOk={confirmCreateSpace} onCancel={() => setSpaceOpen(false)} okText="Create">
        <Input placeholder="space name (e.g. a community)" value={value} onChange={(e) => setValue(e.target.value)} onPressEnter={confirmCreateSpace} autoFocus />
      </Modal>
    </div>
  );
}
